/**
 * Хранилище секретов.
 *
 * Пароли — от Яндекс.Диска и от резервных копий — лежали в той же базе, что и
 * задачи. Это неправильное соседство: базу приложения читает всё, что имеет
 * доступ к устройству, а на телефоне для секретов есть отдельное место,
 * защищённое системой.
 *
 * - **iOS** — Keychain: данные шифруются ключом, привязанным к устройству, и
 *   не попадают в резервную копию iTunes без явного разрешения.
 * - **Android** — EncryptedSharedPreferences поверх Keystore: ключ живёт в
 *   аппаратном хранилище и не покидает его даже при рутировании.
 *
 * ## В браузере такого места нет
 *
 * И это не повод делать вид, что есть. Во вкладке секрет остаётся в базе
 * приложения — ровно там же, где и был. Меняется не защита, а честность: тот,
 * кто читает интерфейс, должен знать, что в браузере пароль защищён так же,
 * как остальные данные, то есть учётной записью системы и ничем больше.
 *
 * Поэтому `isSecureStorage()` отвечает не «получилось ли сохранить», а «лежит
 * ли секрет в защищённом системой месте» — на этот вопрос интерфейс и
 * ссылается.
 */

import { Capacitor } from '@capacitor/core';

import { db } from '@/shared/db/database';

/** Секрет лежит в системном хранилище, а не в базе приложения. */
export function isSecureStorage(): boolean {
  return Capacitor.isNativePlatform();
}

export async function readSecret(key: string): Promise<string | null> {
  if (!isSecureStorage()) return readFromDatabase(key);

  try {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    const value = await SecureStorage.getItem(key);
    if (value !== null) return value;

    /*
      Секрета в системном хранилище нет — возможно, он остался в базе от
      прежних версий. Тогда он переезжает прямо сейчас: молча потерять
      сохранённый пароль хуже, чем хранить его лишний день не там.
    */
    const legacy = await readFromDatabase(key);
    if (legacy !== null) await writeSecret(key, legacy);

    return legacy;
  } catch {
    // Хранилище может быть недоступно — например, пока устройство не
    // разблокировали ни разу после перезагрузки. Это не повод падать.
    return readFromDatabase(key);
  }
}

export async function writeSecret(key: string, value: string): Promise<void> {
  if (!isSecureStorage()) {
    await db.settings.put({ key, value, updatedAt: Date.now() });
    return;
  }

  const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
  await SecureStorage.setItem(key, value);

  // Копия в базе, если она там была, больше не нужна — и оставлять её значит
  // сохранять ровно ту дыру, ради которой всё затевалось.
  await db.settings.delete(key);
}

export async function removeSecret(key: string): Promise<void> {
  await db.settings.delete(key);
  if (!isSecureStorage()) return;

  try {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    await SecureStorage.remove(key);
  } catch {
    // Ключа могло и не быть — цель достигнута.
  }
}

async function readFromDatabase(key: string): Promise<string | null> {
  const row = await db.settings.get(key);
  return typeof row?.value === 'string' && row.value !== '' ? row.value : null;
}
